import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  MessageSquare,
  Calendar,
  Clock,
  Search,
  History,
  X,
  PlusCircle,
  ListPlus,
  RefreshCcw,
  Paperclip,
  Send,
  Sparkles,
  Image,
  FileText,
  File,
  MoreVertical,
  Filter,
  AlertCircle,
} from "lucide-react";
import { Ticket as TicketType, TicketResposta } from "@/types";
import TicketResponse from "./TicketResponse";
import QuillEditor from "./QuillEditor";
import AIAssistantModal from "../AIAssistantModal";
import TemplateSelector from "./TemplateSelector";
import TemplateDropdown from "./TemplateDropdown";
import Modal from "../ui/Modal";
import UserMention from "./UserMention";

interface TicketResponsesProps {
  ticket: TicketType;
  usuario: any;
  editingMessageId: number | null;
  editingContent: string;
  onEdit: (resposta: TicketResposta) => void;
  onDelete: (resposta: TicketResposta) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onContentChange: (content: string) => void;
  onDownload: (anexo: any) => void;
  onView: (anexo: any) => void;
  canPreview: (anexo: any) => boolean;
  formatDate: (dateString: string) => string;
  renderUserInfo: (resposta: TicketResposta) => React.ReactNode;
  orderAsc: boolean;
  setOrderAsc: (v: boolean) => void;
  onShowSystemMessages?: () => void;
  systemMessagesCount?: number;
  // Paginação incremental
  visibleCount: number;
  totalUserResponses: number;
  onLoadMore: () => void;
  onShowAll: () => void;
  onResetVisibleCount: () => void;
  // Props para formulário de chat
  newResponse: string;
  anexos: File[];
  isSubmitting: boolean;
  configuracoes: {
    arquivos_permitidos: string;
    maximo_arquivos: string;
    tamanho_maximo: string;
  };
  onResponseChange: (content: string) => void;
  onFileSelect: (files: FileList | null) => void;
  onRemoveAnexo: (index: number) => void;
  onSubmit: () => void;
  onAITextImprovement: (improvedText: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  isDragOver: boolean;
  ticketId?: number;
  departamentoId?: number;
  podeEnviarResposta?: boolean;
}

export default function TicketResponses({
  ticket,
  usuario,
  editingMessageId,
  editingContent,
  onEdit,
  onDelete,
  onCancelEdit,
  onSaveEdit,
  onContentChange,
  onDownload,
  onView,
  canPreview,
  formatDate,
  renderUserInfo,
  orderAsc,
  setOrderAsc,
  onShowSystemMessages,
  systemMessagesCount,
  visibleCount,
  totalUserResponses,
  onLoadMore,
  onShowAll,
  onResetVisibleCount,
  newResponse,
  anexos,
  isSubmitting,
  configuracoes,
  onResponseChange,
  onFileSelect,
  onRemoveAnexo,
  onSubmit,
  onAITextImprovement,
  onDragOver,
  onDragLeave,
  onDrop,
  isDragOver,
  ticketId,
  departamentoId,
  podeEnviarResposta = true,
}: TicketResponsesProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [showAIModal, setShowAIModal] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showFiltersMenu, setShowFiltersMenu] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const filtersMenuRef = useRef<HTMLDivElement>(null);
  const templateButtonRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<any>(null);

  // Fechar menus ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        actionsMenuRef.current &&
        !actionsMenuRef.current.contains(event.target as Node)
      ) {
        setShowActionsMenu(false);
      }
      if (
        filtersMenuRef.current &&
        !filtersMenuRef.current.contains(event.target as Node)
      ) {
        setShowFiltersMenu(false);
      }
      if (
        templateButtonRef.current &&
        !templateButtonRef.current.contains(event.target as Node)
      ) {
        setShowTemplateDropdown(false);
      }
    };

    if (showActionsMenu || showFiltersMenu || showTemplateDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showActionsMenu, showFiltersMenu, showTemplateDropdown]);


  // Separar mensagens do sistema das mensagens normais
  // O backend já filtra mensagens do sistema (usuario_id = 1) das respostas
  // As respostas já vêm apenas com mensagens de usuários reais
  const userRespostas = ticket.respostas || [];
  const systemRespostas: any[] = []; // Não usado mais, mantido para compatibilidade

  // Filtrar respostas baseado no termo de busca (apenas mensagens de usuários)
  const filteredRespostas = useMemo(() => {
    if (!searchTerm.trim()) return userRespostas;

    const term = searchTerm.toLowerCase();
    return userRespostas.filter((resposta) => {
      // Buscar no conteúdo da resposta
      const content = (resposta.conteudo || "").toLowerCase();
      // Buscar no nome do usuário
      const userName = (resposta.usuario_nome || "").toLowerCase();

      return content.includes(term) || userName.includes(term);
    });
  }, [userRespostas, searchTerm]);

  const showingCount = Math.min(visibleCount || 0, totalUserResponses || 0);
  const isTicketFechado = ticket.status_id === 2 || ticket.ticket_fechado;
  const mostrarAlerta = ticket.mostrar_alerta_reabertura || (isTicketFechado && ticket.pode_responder);

  const quillConfig = {
    modules: {
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        ["link", "image"],
        ["clean"],
      ],
      clipboard: {
        matchVisual: false,
      },
      keyboard: {
        bindings: {
          tab: false,
        },
      },
    },
    formats: [
      "header",
      "bold",
      "italic",
      "underline",
      "strike",
      "list",
      "bullet",
      "color",
      "background",
      "align",
      "link",
      "image",
      "mention", // Adicionar o formato de menção
    ],
    theme: "snow",
    placeholder: "Digite sua mensagem...",
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith("image/"))
      return <Image className="text-blue-500" size={16} />;
    if (file.type === "application/pdf")
      return <FileText className="text-red-500" size={16} />;
    return <File className="text-gray-500" size={16} />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleSubmit = () => {
    // Se o ticket está fechado, mostrar modal de confirmação
    if (mostrarAlerta) {
      setShowReopenModal(true);
      return;
    }
    
    // Caso contrário, enviar diretamente
    onSubmit();
    // Scroll para o final após enviar
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const handleConfirmReopen = () => {
    setShowReopenModal(false);
    onSubmit();
    // Scroll para o final após enviar
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  return (
    <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-gray-200/60 dark:border-gray-700/60 shadow-lg overflow-hidden flex flex-col" style={{ maxHeight: "800px", height: "auto" }}>
      {/* Header fixo */}
      <div className="px-3 sm:px-4 md:px-6 pt-3 sm:pt-4 md:pt-5 pb-3 sm:pb-4 border-b border-gray-200/60 dark:border-gray-700/60 bg-gradient-to-r from-gray-50/50 dark:from-gray-900/50 to-white dark:to-gray-800">
        <div className="flex items-center justify-between gap-2 sm:gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
              <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5 sm:mr-2 text-blue-600 dark:text-blue-400 flex-shrink-0" />
              <span className="truncate">Respostas ({totalUserResponses || 0})</span>
            </h2>
          </div>

          {/* Botão de Filtros */}
          <div className="flex items-center gap-2">
            {/* Botão de Timeline do Ticket */}
            {onShowSystemMessages && (
              <button
                onClick={onShowSystemMessages}
                className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent flex items-center gap-1.5 sm:gap-2 whitespace-nowrap"
                title="Ver timeline do ticket"
              >
                <History className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Timeline</span>
                <span className="sm:hidden">Timeline</span>
              </button>
            )}

            {/* Botão de Filtros */}
            <div className="relative" ref={filtersMenuRef}>
              <button
                onClick={() => setShowFiltersMenu(!showFiltersMenu)}
                className={`p-2 sm:p-2.5 rounded-lg sm:rounded-xl transition-all duration-200 border shadow-sm hover:shadow-md ${
                  showFiltersMenu || searchTerm
                    ? "bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 border-blue-300 dark:border-blue-700"
                    : "bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 border-gray-200 dark:border-gray-600"
                }`}
                title="Filtros e busca"
              >
                <Filter className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>

              {/* Menu de Filtros */}
              {showFiltersMenu && (
                <div className="absolute top-full right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 z-50">
                    {/* Campo de busca */}
                    <div className="mb-4">
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Buscar nas mensagens
                      </label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder="Buscar nas mensagens..."
                          className="w-full pl-10 pr-10 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
                        />
                        {searchTerm && (
                          <button
                            onClick={() => setSearchTerm("")}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
                            title="Limpar busca"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Ordenação */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Ordenar por
                      </label>
                      <div className="inline-flex bg-gray-100 dark:bg-gray-700 rounded-md p-0.5 w-full">
                        <button
                          onClick={() => {
                            setOrderAsc(false);
                            setShowFiltersMenu(false);
                          }}
                          className={`flex-1 px-3 py-1.5 text-sm rounded transition-colors ${
                            !orderAsc
                              ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm"
                              : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                          }`}
                          aria-pressed={!orderAsc}
                        >
                          Mais recentes
                        </button>
                        <button
                          onClick={() => {
                            setOrderAsc(true);
                            setShowFiltersMenu(false);
                          }}
                          className={`flex-1 px-3 py-1.5 text-sm rounded transition-colors ${
                            orderAsc
                              ? "bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm"
                              : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                          }`}
                          aria-pressed={orderAsc}
                        >
                          Mais antigos
                        </button>
                      </div>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>

      {/* Corpo rolável apenas para mensagens */}
      {!filteredRespostas || filteredRespostas.length === 0 ? (
        <div className="text-gray-500 dark:text-gray-400 text-center py-16 px-6">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-gray-100 dark:from-gray-700 to-gray-200 dark:to-gray-600 flex items-center justify-center">
            <MessageSquare className="w-10 h-10 text-gray-400 dark:text-gray-500" />
          </div>
          {searchTerm ? (
            <>
              <p className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Nenhuma mensagem encontrada
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Tente outro termo de busca</p>
            </>
          ) : (
            <>
              <p className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-1">
                Nenhuma resposta ainda
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Seja o primeiro a responder!
              </p>
            </>
          )}
        </div>
      ) : (
        <div
          className="px-2 sm:px-4 md:px-6 py-3 sm:py-4 responses-scrollbar bg-gradient-to-b from-gray-50/30 dark:from-gray-900/30 to-white dark:to-gray-800"
          style={{ 
            flex: "1 1 auto", 
            maxHeight: "500px",
            overflowY: "auto",
            minHeight: "0"
          }}
        >
          <div className="space-y-1">
            {filteredRespostas.map((resposta, index) => {
              // Identificar a primeira mensagem (primeira resposta não-sistema ordenada por data)
              const userResponses = filteredRespostas.filter(
                (r) => !r.usuario_nome?.toLowerCase().includes("sistema")
              );
              const sortedUserResponses = [...userResponses].sort((a, b) => {
                const dateA = new Date(a.created_at || 0).getTime();
                const dateB = new Date(b.created_at || 0).getTime();
                if (dateA !== dateB) return dateA - dateB;
                return (a.id || 0) - (b.id || 0);
              });
              const isFirstMessage = sortedUserResponses.length > 0 && 
                sortedUserResponses[0].id === resposta.id &&
                !resposta.usuario_nome?.toLowerCase().includes("sistema");

              return (
                <div key={resposta.id} className="message-item">
                  <TicketResponse
                    resposta={resposta}
                    usuario={usuario}
                    isEditing={editingMessageId === resposta.id}
                    editingContent={editingContent}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onCancelEdit={onCancelEdit}
                    onSaveEdit={onSaveEdit}
                    onContentChange={onContentChange}
                    onDownload={onDownload}
                    onView={onView}
                    canPreview={canPreview}
                    formatDate={formatDate}
                    renderUserInfo={renderUserInfo}
                    isFirstMessage={isFirstMessage}
                    allResponses={filteredRespostas}
                  />
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        </div>
      )}

      {/* Formulário de Chat Integrado */}
      {ticket.pode_responder && (
        <div className="border-t border-gray-200/60 dark:border-gray-700/60 bg-white dark:bg-gray-800 flex-shrink-0">
          {/* Alerta quando operador não pode responder porque ticket está aberto */}
          {!podeEnviarResposta && usuario && (usuario.nivel === 'admin' || usuario.nivel === 'dev') && ticket.status_id === 1 && (
            <div className="px-2 sm:px-3 md:px-4 py-3 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800">
              <div className="flex items-center gap-2 text-sm text-yellow-800 dark:text-yellow-200">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>
                  Você precisa <strong>iniciar o atendimento</strong> antes de enviar uma resposta.
                </span>
              </div>
            </div>
          )}
          {/* Anexos selecionados */}
          {anexos.length > 0 && (
            <div className="px-2 sm:px-3 md:px-4 pt-2 sm:pt-3 pb-2 sm:pb-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 overflow-hidden">
                <div className="flex flex-wrap gap-2">
                  {anexos.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 bg-white dark:bg-gray-700 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-600 shadow-sm hover:shadow-md transition-shadow"
                    >
                      {getFileIcon(file)}
                      <span className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate max-w-[150px]">
                        {file.name}
                      </span>
                      <button
                        onClick={() => onRemoveAnexo(index)}
                        className="text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors p-0.5 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                        title="Remover anexo"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Área de upload de anexos (expandível) */}
          {showAttachments && (
            <div
              className={`overflow-hidden border-b border-gray-200 dark:border-gray-700 ${
                isDragOver ? "bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700" : "bg-white dark:bg-gray-800"
              }`}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
            >
                <div className="px-2 sm:px-3 md:px-4 py-2 sm:py-3 md:py-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={(e) => onFileSelect(e.target.files)}
                    className="hidden"
                    id="file-upload-chat"
                    accept={configuracoes.arquivos_permitidos
                      .split(",")
                      .map((ext) => `.${ext.trim()}`)
                      .join(",")}
                  />
                  <label
                    htmlFor="file-upload-chat"
                    className="cursor-pointer flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 font-medium"
                  >
                    <div className="p-1.5 sm:p-2 bg-gray-100 dark:bg-gray-700 rounded-lg flex-shrink-0">
                      <Paperclip className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-600 dark:text-gray-400" />
                    </div>
                    <span className="break-words">
                      Clique para anexar ou arraste arquivos aqui (máx.{" "}
                      {configuracoes.maximo_arquivos} arquivos,{" "}
                      {configuracoes.tamanho_maximo}MB cada)
                    </span>
                  </label>
                </div>
              </div>
            )}

          {/* Input de mensagem estilo chat */}
          <div className="px-2 sm:px-3 md:px-4 py-2 sm:py-3 md:py-4 bg-gray-50/50 dark:bg-gray-900/50">
            <div className="space-y-2 sm:space-y-3">
              {/* Editor Quill compacto */}
              <div className="w-full">
                <div className="quill-wrapper-chat">
                  <QuillEditor
                    ref={quillRef}
                    value={newResponse}
                    onChange={onResponseChange}
                    {...quillConfig}
                    className="bg-white dark:bg-gray-700 rounded-lg sm:rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm"
                    readOnly={!podeEnviarResposta}
                  />
                </div>
              </div>

              {/* Botões de ação */}
              <div className="flex items-center justify-end gap-1.5 sm:gap-2 md:gap-3">
                {/* Botão de Menção */}
                <UserMention
                  currentUserId={usuario?.id}
                  usuariosMencionadosIds={ticket?.usuarios_mencionados_ids || []}
                  onMention={(userId, userName) => {
                    if (quillRef.current) {
                      const quill = quillRef.current.getEditor();
                      if (quill) {
                        const range = quill.getSelection(true);
                        
                        try {
                          // Usar o Blot customizado para inserir a menção
                          // Inserir um caractere vazio ou espaço, pois o Blot vai criar todo o conteúdo
                          const Quill = (quill as any).constructor;
                          const Delta = Quill.import('delta');
                          const mentionDelta = new Delta()
                            .retain(range ? range.index : quill.getLength() - 1)
                            .insert('\uFEFF', { mention: { userId, userName } }); // Zero-width no-break space
                          
                          quill.updateContents(mentionDelta, 'user');
                          // Ajustar seleção para depois da menção (1 caractere)
                          quill.setSelection((range ? range.index : quill.getLength() - 1) + 1);
                        } catch (error) {
                          const mentionText = `@${userName}`;
                          if (range) {
                            quill.insertText(range.index, mentionText);
                            quill.setSelection(range.index + mentionText.length);
                          } else {
                            const length = quill.getLength();
                            quill.insertText(length - 1, mentionText);
                            quill.setSelection(length - 1 + mentionText.length);
                          }
                        }
                      }
                    }
                  }}
                  disabled={isSubmitting}
                />
                
                {/* Botão de Template */}
                {ticketId && (
                  <div className="relative" ref={templateButtonRef}>
                    <button
                      onClick={() => {
                        setShowTemplateDropdown(!showTemplateDropdown);
                        setShowActionsMenu(false);
                      }}
                      className={`p-2 sm:p-2.5 rounded-lg sm:rounded-xl transition-all duration-200 border shadow-sm hover:shadow-md ${
                        showTemplateDropdown
                          ? "bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-300 dark:border-blue-600"
                          : "bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-600 border-gray-200 dark:border-gray-600"
                      }`}
                      title="Usar Template"
                    >
                      <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                    {showTemplateDropdown && (
                      <TemplateDropdown
                        onSelect={async (conteudo) => {
                          if (!conteudo) return;
                          
                          // Atualizar o estado - o ReactQuill deve detectar a mudança
                          onResponseChange(conteudo);
                          
                          // Aguardar um pouco e então forçar atualização no Quill se necessário
                          setTimeout(() => {
                            if (quillRef.current) {
                              const quill = quillRef.current.getEditor();
                              if (quill && conteudo) {
                                const currentContent = quill.root.innerHTML.trim();
                                const newContent = conteudo.trim();
                                
                                // Só atualizar se o conteúdo for diferente
                                if (currentContent !== newContent) {
                                  try {
                                    // Usar root.innerHTML para aplicar o HTML diretamente
                                    quill.root.innerHTML = conteudo;
                                  } catch (error) {
                                    // Erro ao aplicar template, conteúdo já foi atualizado no estado
                                  }
                                }
                              }
                            }
                          }, 100);
                          
                          setShowTemplateDropdown(false);
                        }}
                        onClose={() => setShowTemplateDropdown(false)}
                      />
                    )}
                  </div>
                )}

                {/* Botão de Ações (Menu) */}
                <div className="relative" ref={actionsMenuRef}>
                  <button
                    onClick={() => setShowActionsMenu(!showActionsMenu)}
                    className={`p-2 sm:p-2.5 rounded-lg sm:rounded-xl transition-all duration-200 border shadow-sm hover:shadow-md ${
                      showActionsMenu
                        ? "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-100 dark:border-gray-600"
                        : "bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 border-gray-200 dark:border-gray-600"
                    }`}
                    title="Mais ações"
                  >
                    <MoreVertical className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>

                  {/* Menu de Ações */}
                  {showActionsMenu && (
                    <div className="absolute bottom-full right-0 mb-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                        {/* Botão IA */}
                        <button
                          onClick={() => {
                            setShowAIModal(true);
                            setShowActionsMenu(false);
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 transition-colors"
                        >
                          <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                          <span>Assistente de IA</span>
                        </button>

                        {/* Botão Anexos */}
                        <button
                          onClick={() => {
                            setShowAttachments(!showAttachments);
                            setShowActionsMenu(false);
                          }}
                          className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 transition-colors ${
                            showAttachments
                              ? "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30"
                              : "text-gray-700 dark:text-gray-300"
                          }`}
                        >
                          <Paperclip className="w-4 h-4" />
                          <span>{showAttachments ? "Ocultar Anexos" : "Anexar Arquivo"}</span>
                        </button>
                      </div>
                    )}
                </div>

                {/* Botão enviar */}
                <button
                  disabled={
                    isSubmitting || 
                    (!newResponse.trim() && anexos.length === 0) ||
                    !podeEnviarResposta
                  }
                  onClick={handleSubmit}
                  className={`px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 rounded-lg sm:rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-1.5 sm:gap-2 shadow-md hover:shadow-lg ${
                    mostrarAlerta
                      ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                      : "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"
                  }`}
                  title={
                    mostrarAlerta
                      ? "Enviar mensagem e reabrir ticket"
                      : "Enviar mensagem"
                  }
                >
                  {isSubmitting ? (
                    <div className="animate-spin rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <>
                      <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span className="text-xs sm:text-sm font-medium">Enviar</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Template */}
      {showTemplateSelector && ticketId && (
        <TemplateSelector
          ticketId={ticketId}
          departamentoId={departamentoId}
          onSelect={(conteudo) => {
            onResponseChange(conteudo);
            setShowTemplateSelector(false);
          }}
          onClose={() => setShowTemplateSelector(false)}
        />
      )}

      {/* Modal de Assistente de IA */}
      <AIAssistantModal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        onApplyText={onAITextImprovement}
        originalText={newResponse}
        placeholder="Ex: Melhore este texto, torne-o mais profissional, corrija erros gramaticais, etc..."
      />

      {/* Modal de Confirmação de Reabertura */}
      {showReopenModal && (
        <Modal
          isOpen={showReopenModal}
          onClose={() => !isSubmitting && setShowReopenModal(false)}
          title="Reabrir Ticket"
          maxWidth="md"
          closeOnBackdropClick={!isSubmitting}
        >
          <div className="p-4 sm:p-6">
            <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-amber-100 dark:from-amber-900/50 to-orange-100 dark:to-orange-900/50 rounded-full flex items-center justify-center">
                  <svg className="h-5 w-5 sm:h-6 sm:w-6 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100 mb-1.5 sm:mb-2 flex items-center gap-1.5 sm:gap-2 flex-wrap">
                  <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-xs font-semibold bg-amber-200 dark:bg-amber-900/50 text-amber-900 dark:text-amber-300">
                    ATENÇÃO
                  </span>
                  <span className="truncate">Ticket Fechado</span>
                </h3>
                <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                  <strong className="font-semibold text-gray-900 dark:text-gray-100">Este ticket está fechado.</strong>
                </p>
                <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 mt-1.5 sm:mt-2 leading-relaxed">
                  Ao enviar esta mensagem, o ticket será <strong className="text-amber-600 dark:text-amber-400">automaticamente reaberto</strong> com status <strong className="text-amber-600 dark:text-amber-400">"Em Andamento"</strong>.
                </p>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-2 sm:mt-3 font-medium">
                  Deseja continuar e reabrir o ticket?
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4 sm:mt-6">
              <button
                onClick={() => setShowReopenModal(false)}
                disabled={isSubmitting}
                className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm sm:text-base"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmReopen}
                disabled={isSubmitting}
                className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 dark:from-amber-600 dark:to-orange-600 hover:from-amber-600 hover:to-orange-600 dark:hover:from-amber-700 dark:hover:to-orange-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium shadow-sm hover:shadow-md text-sm sm:text-base"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 border-2 border-white border-t-transparent mr-1.5 sm:mr-2"></div>
                    <span>Enviando...</span>
                  </div>
                ) : (
                  "Sim, Enviar e Reabrir"
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Footer fixo com paginação - apenas se houver mais mensagens */}
      {showingCount < (totalUserResponses || 0) && (
        <div className="px-4 sm:px-6 py-3 border-t border-gray-200/60 dark:border-gray-700/60 bg-gradient-to-r from-white dark:from-gray-800 to-gray-50/50 dark:to-gray-900/50">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
              Mostrando <span className="font-semibold text-gray-900 dark:text-gray-100">{showingCount}</span> de <span className="font-semibold text-gray-900 dark:text-gray-100">{totalUserResponses || 0}</span> respostas
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onLoadMore}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all duration-200 text-xs sm:text-sm font-medium border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-400 dark:hover:border-gray-500 shadow-sm hover:shadow"
                title="Carregar mais 10 respostas"
              >
                <PlusCircle className="w-4 h-4" />
                <span>Carregar Mais</span>
              </button>
              <button
                onClick={onShowAll}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all duration-200 text-xs sm:text-sm font-medium bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600 shadow-sm hover:shadow"
                title="Mostrar todas as respostas"
              >
                <ListPlus className="w-4 h-4" />
                <span>Mostrar Tudo</span>
              </button>
              {visibleCount > 10 && (
                <button
                  onClick={onResetVisibleCount}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md transition-all duration-200 text-xs sm:text-sm font-medium bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-400 dark:hover:border-gray-500 shadow-sm hover:shadow"
                  title="Voltar para exibição padrão"
                >
                  <RefreshCcw className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
